import type { IconName } from '../components/Icon';
import { Icon } from '../components/Icon';
import { PageHeader } from '../components/Shell';
import { NavLink } from '../lib/router';
import { SCHEDULE_PATH } from '../lib/navigation';

export interface TutorialStep {
  action: string;
  description: string;
  note: string;
  title: string;
  to: string;
}

export interface FeatureGuide {
  guardrail: string;
  icon: IconName;
  id: string;
  summary: string;
  title: string;
  to: string;
  useWhen: string;
}

export const tutorialSteps: TutorialStep[] = [
  {
    title: 'Check growth and readiness',
    description: 'Start on Today. Scan the six work queues, select a priority check, and use the adjacent company preview and next action. Recent signals show initial outreach over seven complete UTC days.',
    note: 'An enabled scheduler is not proof that outreach or publishing is ready.',
    action: 'Open Today',
    to: '/'
  },
  {
    title: 'Inspect the outcomes',
    description: 'Review sent outreach, recorded replies and self-service milestones. Open Metrics when you need their source, population and time window, or follow the links to inspect a result.',
    note: 'These are separate populations. Platform signups and activity are not yet attributed to GTM.',
    action: 'Inspect Metrics',
    to: '/metrics'
  },
  {
    title: 'Check the Director queue',
    description: 'Open the compact Director queue on Today. Compare review stages, the next scheduled check and any blocker, then follow the relevant queue for its evidence.',
    note: 'The next check is not a promised completion time. Completed work, approvals and accepted emails remain separate states.',
    action: 'Open Director queue',
    to: '/?panel=director'
  }
];

export const manualSteps: TutorialStep[] = [
  {
    title: 'Check the operating picture',
    description: 'Start on Today. Check queued work and observed outcomes. Schedule and source details open below when you need the evidence.',
    note: 'A missing observation is deliberately different from zero.',
    action: 'Open Today',
    to: '/'
  },
  {
    title: 'Refresh aggregate evidence',
    description: 'Use Metrics to inspect provenance and UTC windows. If your role allows it, Collect now starts one bounded collection job.',
    note: 'Collection does not run an agent and cannot publish content.',
    action: 'Open Metrics',
    to: '/metrics'
  },
  {
    title: 'Run one focused agent',
    description: 'Browse the paginated run history by agent, status or UTC date. Open Agent controls and schedule to choose an agent, review the confirmation, and start one bounded run.',
    note: 'Use aggregate evidence only—never paste credentials or personal identifiers.',
    action: 'Open Agents / Runs',
    to: SCHEDULE_PATH
  },
  {
    title: 'Read the generated analysis',
    description: 'Search the report library by topic, agent or date. Brief summaries and daily groups help you find the relevant result without scrolling through the archive.',
    note: 'A report is analysis, not an approval or publication instruction.',
    action: 'Open Reports',
    to: '/reports'
  },
  {
    title: 'Follow approved team work',
    description: 'Team work opens on Needs attention. Switch to Active work or Completed / closed for other records. Select a record for its Director decision and results; expand Activity history for the full trail. Work controls contains the retrospective and batch actions.',
    note: 'A saved draft is distinct from publication. Each action needs its own Director approval.',
    action: 'Open Team work',
    to: '/work'
  },
  {
    title: 'Discover and qualify prospects',
    description: 'Select a prospect and read Next steps. Generate preview starts one tracked image job; Provide a business contact saves a verified company-domain address securely. Rejected, suppressed and already-contacted records explain why their actions are locked. Open Find new prospects for a new research pass.',
    note: 'A generated preview or saved contact does not send email. Qualification requires a business contact and a ready concept; independent Director review and delivery limits still apply.',
    action: 'Open Prospects',
    to: '/prospects?panel=discovery'
  },
  {
    title: 'Run a tailored follow-up campaign',
    description: 'Open Campaigns. Select a step on the graphical sequence to edit its content and timing. Use the arrow controls or Alt + Left/Right to reorder. Save the sequence, then open Progress to preview and explicitly add relationships. Earlier preview recipients can begin at email 2.',
    note: 'Saving a sequence never sends. Each email is drafted and independently reviewed when due. Existing enrollments keep their saved version. Initial outreach and campaign follow-ups share the daily allowance shown in Prospects, with up to 2 attempts per cycle. Failed or uncertain attempts still count; the allowance resets at midnight UTC.',
    action: 'Open Campaigns',
    to: '/campaigns'
  },
  {
    title: 'Manage relationships without hunting through history',
    description: 'Choose a CRM record for its next actions. Open the stage editor, private contacts, notes or history only when needed. Data tools contains saved views and a CSV import preview; contact details are optional in exports. Campaign Progress shows next steps, due times, saved messages and the reason for any blocker.',
    note: 'Replies, opt-outs, bounces, human takeover and Customer/Lost stages stop further marketing. Pause stops new claims; it cannot recall an accepted email. Uncertain delivery is checked against the mailbox, never blindly resent.',
    action: 'Open relationships',
    to: '/crm'
  },
  {
    title: 'Turn recommendations into measured work',
    description: 'Create an experiment for a testable hypothesis, or add an item to Backlog when the work is not yet ready to measure.',
    note: 'Final experiment decisions should cite persisted metric or commercial evidence IDs.',
    action: 'Open Experiments',
    to: '/experiments'
  },
  {
    title: 'Check the editorial review',
    description: 'Publishing opens on website items needing review. Select an item to read the saved article and see its next action. Edit source and technical approval evidence open only when needed. Approved is not the same as published.',
    note: 'Any source edit invalidates an earlier approval automatically; manual review remains available.',
    action: 'Open Publishing',
    to: '/content'
  },
  {
    title: 'Publish deliberately and verify',
    description: 'Confirm the separate reviewer decision and publication result in Activity. Open an event for its underlying evidence. For manual items, use the separate send action only after exact-payload approval.',
    note: 'The generating agent cannot approve its own work.',
    action: 'Open Activity',
    to: '/audit'
  }
];

export const featureGuides: FeatureGuide[] = [
  {
    id: 'overview', title: 'Today', icon: 'overview', to: '/',
    summary: 'The owner snapshot for work queues, Director decisions, next scheduled checks and recorded outreach signals.',
    useWhen: 'Beginning a session or checking whether the system is ready for work.',
    guardrail: 'Missing metrics stay visibly missing; the console never silently treats them as zero.'
  },
  {
    id: 'metrics', title: 'Metrics', icon: 'metrics', to: '/metrics',
    summary: 'A ledger of bounded aggregate observations, including source, population, UTC window, freshness, and values.',
    useWhen: 'Checking evidence or collecting a fresh 7, 30, or 90-day window.',
    guardrail: 'Collect now is explicit, role-gated, and separate from agent execution.'
  },
  {
    id: 'runs', title: 'Agents / Runs', icon: 'runs', to: '/runs',
    summary: 'Starts one named GTM agent with an optional bounded instruction and shows execution history and job state.',
    useWhen: 'You want analysis, recommendations, or a draft based on current aggregate evidence.',
    guardrail: 'Runs may incur model cost, do not retry automatically, and must not receive secrets or personal data.'
  },
  {
    id: 'reports', title: 'Reports', icon: 'reports', to: '/reports',
    summary: 'A searchable library of brief summaries, grouped by UTC day and paginated, with private reports rendered as sanitized Markdown.',
    useWhen: 'Reviewing what an agent concluded and deciding what should become measured work.',
    guardrail: 'Reports are advisory and never execute actions by themselves.'
  },
  {
    id: 'work', title: 'Team work', icon: 'backlog', to: '/work',
    summary: 'Tracks Director-reviewed deliverables, backlog changes and versioned operating guidance proposed by the retrospective agent.',
    useWhen: 'Following a recommendation through execution or checking how the team improved after reviewing completed work.',
    guardrail: 'Approval binds the exact proposed action. Changed evidence baselines block execution; failed or interrupted work requires a fresh review.'
  },
  {
    id: 'prospects', title: 'Prospects', icon: 'runs', to: '/prospects',
    summary: 'Researches white-label partners and Irish venues, creates private branded guest-Wi-Fi concepts, finds public business role contacts, and runs reviewed initial outreach. An operator can also supply a verified named business contact on the company domain.',
    useWhen: 'Building a partner channel or testing the direct managed-hotspot offer with Irish business venues.',
    guardrail: 'Concepts are watermarked and never imply a customer relationship. Contact addresses stay outside model context and encrypted at rest; venue reservations, opt-outs, bounces, or uncertain sends stop conflicting automation.'
  },
  {
    id: 'relationships', title: 'Relationships', icon: 'relationships', to: '/crm',
    summary: 'A compact company workspace for stages, outreach readiness, email engagement, private contacts, notes and owner tasks.',
    useWhen: 'Following an opportunity or resolving the next action without losing the selected company.',
    guardrail: 'Changing a relationship stage never approves an email or clears a marketing stop.'
  },
  {
    id: 'campaigns', title: 'Campaigns', icon: 'campaign', to: '/campaigns',
    summary: 'Builds graphical outreach sequences and separates planned steps from recorded enrollment and delivery progress.',
    useWhen: 'Preparing tailored follow-ups or inspecting the due step and blocker for an enrolled relationship.',
    guardrail: 'Saving a sequence does not enroll or send. Existing enrollments retain their saved sequence version.'
  },
  {
    id: 'previews', title: 'Preview gallery', icon: 'smartphone', to: '/previews',
    summary: 'A private gallery of saved guest-Wi-Fi concepts available for viewing and download.',
    useWhen: 'Reviewing available image concepts. Open Relationships for a specific prospect’s current preview and recovery controls.',
    guardrail: 'Viewing or downloading an image does not approve outreach or send an email.'
  },
  {
    id: 'emails', title: 'Support inbox', icon: 'content', to: '/emails',
    summary: 'Groups inbound support replies, independent Director decisions and Google send outcomes by customer conversation.',
    useWhen: 'Reviewing customer communication or handing a conversation to a human.',
    guardrail: 'Google authorization is required. Every reply needs exact-message approval; uncertain sends are reconciled without automatic retries. Verification and password-reset templates are excluded.'
  },
  {
    id: 'experiments', title: 'Experiments', icon: 'experiments', to: '/experiments',
    summary: 'Defines hypotheses, baselines, targets, owners, evidence windows, and an explicit decision date.',
    useWhen: 'A recommendation can be evaluated against a primary metric.',
    guardrail: 'The lifecycle is deliberate, and final outcomes require a rationale plus persisted evidence references.'
  },
  {
    id: 'backlog', title: 'Backlog / Decisions', icon: 'backlog', to: '/backlog',
    summary: 'Keeps proposed work distinct from recorded decisions while preserving ownership and lifecycle history.',
    useWhen: 'Work needs prioritisation or tracking but is not yet a fully specified experiment.',
    guardrail: 'Updates use the current state digest; stale edits are refused instead of overwriting newer work.'
  },
  {
    id: 'content', title: 'Publishing', icon: 'content', to: '/content',
    summary: 'Shows generated drafts, independent Director reviews, immutable approvals, publication state, and manual recovery controls.',
    useWhen: 'Preparing a private Marketing draft or an approved article for the public /news section.',
    guardrail: 'The generator cannot self-approve. Agent or human approval is bound to the exact current digest, and any uncertainty stops in FAILED_REVIEW.'
  },
  {
    id: 'audit', title: 'Activity', icon: 'audit', to: '/audit',
    summary: 'Shows bounded action metadata: actor, action, object, outcome, reason, request ID, and time.',
    useWhen: 'Verifying an operation, investigating a refusal, or reviewing who changed what.',
    guardrail: 'Credentials, prompts, content bodies, and personal data are excluded.'
  },
  {
    id: 'settings', title: 'Settings', icon: 'settings', to: '/settings',
    summary: 'Direct links to outreach controls, Apollo setup, the support mailbox, schedules and source health.',
    useWhen: 'Checking a connection, credit allowance, automation setting or upcoming scheduled work.',
    guardrail: 'Opening a control does not change it. Paid lookups and manual actions keep their existing confirmation and permission checks.'
  }
];

const questions = [
  {
    question: 'Can I see whether an outreach email was read?',
    answer: 'Open a company in Relationships or Prospects, then Emails & engagement. Sent means provider accepted, not delivered. Eligible tracked messages show image and link request times, which can also come from scanners, privacy proxies or forwarded email; they do not prove reading or identity. Historical untracked emails remain unknown, and no signal does not mean unread. Relationship replies are shown separately.'
  },
  {
    question: 'How do I resolve a blocked outreach draft?',
    answer: 'Open the company in Relationships or Prospects. Request Director review starts a review of that company only. An eligible unsent Director rejection offers Revise and re-review: the app keeps the rejected draft, prepares a conservative replacement under the current policy, and requests an independent review. Progress and the result appear on the record. These actions do not send email; a later scheduled cycle can send an approved draft. Opt-outs, owner stops, uncertain delivery and already-sent messages cannot be bypassed.'
  },
  {
    question: 'Why is a business contact being rejected?',
    answer: 'The email must match the company domain displayed beside the form. The source must be a public HTTPS page on that company’s website; it is prefilled where known, and Use company website restores the known address. The form identifies whether the email format, domain or source needs correcting and keeps your entries. An unrelated domain cannot be accepted as the same business without verified company evidence.'
  },
  {
    question: 'Can measurement run without a per-contact consent form?',
    answer: 'The owner-configured disclosed policy removes that app form for eligible new outreach. It includes a measurement notice and opt-out, and preserves earlier recipient opt-outs. Existing sent or approved emails are not silently changed. Collection is separately enabled during deployment; the setting does not establish legal permission. Irish/EU tracking rules may still require recipient consent. Image and link requests never prove that a person read the message.'
  },
  {
    question: 'How do I record permission for email measurement?',
    answer: 'In Emails & engagement, open Recipient measurement consent. Check the exact recipient, enter when they explicitly agreed in UTC, and record 20–1,000 characters of evidence. Permission must be within the past 90 days. Apollo discovery, contact approval and consent to receive email are not measurement consent. Recording permission does not turn collection on or measure old untracked messages; the global gate remains separate. You can withdraw measurement consent here without changing sending permission.'
  },
  {
    question: 'When will an item awaiting Director review be decided?',
    answer: 'Open the Director queue on Today. Its next scheduled check shows when that process should inspect eligible work, not a guaranteed finish time. Missing contact or preview evidence, paused automation, review limits and provider failures can defer an item. Open the queue to inspect the recorded blocker before repeating an action.'
  },
  {
    question: 'How do I generate a preview for one prospect?',
    answer: 'Open Prospects / Outreach, select an unreviewed prospect, and choose Generate preview under Next steps. Confirm the image-API cost. The tracked job updates automatically and can be reopened later. Regenerate preview replaces an existing concept only after success; if generation fails, the previous preview is retained. Approved or sent records are locked to preserve what was reviewed and delivered.'
  },
  {
    question: 'Can I provide a contact when research cannot find one?',
    answer: 'Yes. On an editable prospect, open Provide a business contact. Enter a verified business email on the company domain, its company-owned source page, and confirm it is relevant and not opted out. Named business contacts are supported. The address is encrypted, shown masked after saving, and never included in model prompts. Saving is not sending, but a ready prospect may enter the next independently reviewed outreach cycle.'
  },
  {
    question: 'Why is a control disabled?',
    answer: 'Your server-advertised capability or the current release gate does not allow that action. The UI does not bypass those controls; ask a console administrator if your role should have access.'
  },
  {
    question: 'What should I do when a job fails?',
    answer: 'Read the bounded failure state, check Audit for the matching request, and correct the underlying input or service issue before starting a new job. The console never retries a costly action automatically.'
  },
  {
    question: 'Why was my content approval removed?',
    answer: 'The source payload changed after approval. Review the new content and sources, save it, and approve the new SHA-256 digest before sending.'
  },
  {
    question: 'What is the difference between draft and published?',
    answer: 'Draft sends the approved content into the private Marketing workflow. Published is the explicit public destination for /news and receives a stronger final confirmation.'
  },
  {
    question: 'Which prospect motion should I use?',
    answer: 'Use White-label partner for installers and MSPs that could resell LiquidEdge under their brand. Use Direct Irish venue for a business that may work with its current IT provider or test a compatibility-dependent managed starter hotspot. Research creates drafts and private concepts; it does not send immediately.'
  }
];

export function HelpPage() {
  return <>
    <PageHeader
      eyebrow="Product guide"
      title="Help & tutorial"
      description="Check what the team is achieving, confirm automation is ready, and step in when an exception needs you."
      action={<NavLink className="button" to="/">Return to overview</NavLink>}
    />

    <p className="list-note">Use the main navigation for a workspace, then the compact page links above its heading. Activity includes Team work, Agents / runs, Reports, Experiments and Backlog; Relationships includes Prospects / outreach and the Preview gallery.</p>

    <section aria-labelledby="tutorial-title">
      <div className="section-heading">
        <div><h2 id="tutorial-title">Your regular check-in</h2></div>
        <p>Start with these three checks. Open the manual workflow when you need to intervene.</p>
      </div>
      <ol className="tutorial-flow">
        {tutorialSteps.map((step, index) => <li key={step.title}>
          <span className="tutorial-flow__number">{String(index + 1).padStart(2, '0')}</span>
          <div className="tutorial-flow__copy"><h3>{step.title}</h3><p>{step.description}</p><small>{step.note}</small></div>
          <NavLink className="text-link" to={step.to}>{step.action}<span aria-hidden="true"> →</span></NavLink>
        </li>)}
      </ol>
    </section>

    <details className="panel control-disclosure">
      <summary>Manual workflow and recovery controls</summary>
      <p className="list-note">Scheduled agents research, prepare work and request independent review. Check their results and readiness here, then use the existing controls to resolve exceptions. Each channel still follows its own review and sending settings.</p>
      <p className="list-note">Use the relevant step for a specific task. You do not need to run every stage during a regular check-in.</p>
      <ol className="tutorial-flow">
        {manualSteps.map((step, index) => <li key={step.title}>
          <span className="tutorial-flow__number">{String(index + 1).padStart(2, '0')}</span>
          <div className="tutorial-flow__copy"><h3>{step.title}</h3><p>{step.description}</p><small>{step.note}</small></div>
          <NavLink className="text-link" to={step.to}>{step.action}<span aria-hidden="true"> →</span></NavLink>
        </li>)}
      </ol>
    </details>

    <section className="panel help-reference" aria-labelledby="feature-guide-title">
      <div className="panel__header"><div><p className="eyebrow">Feature reference</p><h2 id="feature-guide-title">What every section does</h2><p>Expand a section for practical guidance and its most important guardrail.</p></div></div>
      <div className="help-feature-grid">
        {featureGuides.map((feature) => <details className="help-feature" id={'help-' + feature.id} key={feature.id}>
          <summary><Icon name={feature.icon} /><span><strong>{feature.title}</strong></span><span aria-hidden="true" className="help-feature__toggle">+</span></summary>
          <div className="help-feature__body">
            <p>{feature.summary}</p>
            <dl><div><dt>Use it when</dt><dd>{feature.useWhen}</dd></div><div><dt>Guardrail</dt><dd>{feature.guardrail}</dd></div></dl>
            <NavLink className="text-link" to={feature.to}>Open {feature.title}<span aria-hidden="true"> →</span></NavLink>
          </div>
        </details>)}
      </div>
    </section>

    <section className="two-column help-bottom">
      <article className="panel">
        <div className="panel__header"><div><p className="eyebrow">Common questions</p><h2>When something looks unfamiliar</h2></div></div>
        <div className="help-faq">
          {questions.map((item) => <details key={item.question}><summary>{item.question}<span aria-hidden="true">+</span></summary><p>{item.answer}</p></details>)}
        </div>
      </article>
      <article className="panel help-boundary">
        <div><p className="eyebrow">Before every agent run</p><h2>Keep instructions bounded</h2></div>
        <ul>
          <li>Use aggregate business evidence only.</li>
          <li>Never paste passwords, tokens, cookies, or connection strings.</li>
          <li>Exclude names, email addresses, IPs, MAC addresses, and visitor-level data.</li>
          <li>Treat generated output as a draft until the independent Director or a human has reviewed it.</li>
        </ul>
        <NavLink className="button button--quiet" to="/runs">Go to Agents / Runs</NavLink>
      </article>
    </section>
  </>;
}
